import { CalciteOption } from "@esri/calcite-components-react";
import { UserContext } from "context/UserContext";
import React, { useEffect } from "react";
import { ALIRecordRequest } from "services/ALIRecord";
import { getUserCPFOptions } from "services/User";
import tw from "twin.macro";
import { Select } from "./Select";

export interface MigrateForm {
  CPF: string;
  CPN: string;
}

interface NenaIDSelectProps {
  recordRequest: ALIRecordRequest | MigrateForm;
  setRecordRequest: React.Dispatch<
    React.SetStateAction<ALIRecordRequest | MigrateForm>
  >;
  dashboard?: boolean;
}

export const NenaIDSelect: React.FC<NenaIDSelectProps> = ({
  recordRequest,
  setRecordRequest,
  dashboard = false,
}) => {
  const { user } = React.useContext(UserContext);
  const [userCPF, setUserCPF] = React.useState<
    { label: string; value: string }[]
  >([]);

  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    setLoading(true);
    getUserCPFOptions(user)
      .then((options) => {
        if (Array.isArray(options)) {
          setUserCPF(
            options.map((cpf) => ({
              label: cpf,
              value: cpf,
            })),
          );
          // If the current CPF in recordRequest is not in the options, set it to the first option
          if (!recordRequest.CPF)
            setRecordRequest((prev: ALIRecordRequest | MigrateForm) => ({
              ...prev,
              CPF: recordRequest.CPF
                ? recordRequest.CPF
                : options.length > 0
                  ? options[0]
                  : "",
            }));
        }
      })
      .catch((error) => {
        console.error("Error fetching NENA IDs:", error);
        setUserCPF([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setRecordRequest, user]);

  return (
    <Select
      label="NENA ID*"
      value={recordRequest.CPF || ""}
      scale={dashboard ? "m" : "s"}
      labelSize={dashboard ? "m" : "s"}
      name="nenaId"
      wrapperCss={dashboard ? [tw`lg:w-1/4`] : undefined}
      onChange={(e) => {
        const value = e.target.value;
        setRecordRequest((prev: ALIRecordRequest | MigrateForm) => ({
          ...prev,
          CPF: value,
        }));
      }}
    >
      {loading && (
        <CalciteOption value="" disabled>
          Loading...
        </CalciteOption>
      )}
      {/* if recordRequest.CPF is not null and is not in options */}
      {recordRequest.CPF &&
        !userCPF.some((option) => option.value === recordRequest.CPF) && (
          <CalciteOption key={recordRequest.CPF} value={recordRequest.CPF}>
            {recordRequest.CPF}
          </CalciteOption>
        )}

      {/* if recordRequest.CPF is in options, put the recordRequest.CPF first option */}
      {recordRequest.CPF &&
        userCPF.some((option) => option.value === recordRequest.CPF) &&
        userCPF
          .sort((a, b) => {
            if (a.value === recordRequest.CPF) return -1;
            if (b.value === recordRequest.CPF) return 1;
            return 0;
          })
          .map((option) => (
            <CalciteOption key={option.value} value={option.value}>
              {option.label}
            </CalciteOption>
          ))}
      {/* else just show all options */}
      {!recordRequest.CPF &&
        userCPF.map((option) => (
          <CalciteOption key={option.value} value={option.value}>
            {option.label}
          </CalciteOption>
        ))}
    </Select>
  );
};

export default NenaIDSelect;
